import { useMemo } from "react";

import { APIResponseType } from "@/shared/types";
import {
  createDeleteApiHook,
  createGetApiHook,
  createPatchApiHook,
  createPostApiHook,
  createPutApiHook,
} from "@/shared/api/apiHooks";
import { normalizeMainCardByIdData } from "@/cards/common/utils/resolveMainCardPages";

// core cards CRUD
const POSTMainCardAPI = `/main-card`;

export type TalentHeader = {
  headers?: Record<string, string>;
};

type cardQueryParams = Record<string, any>;

export const useCreateMainCard = createPostApiHook<{}, TalentHeader, APIResponseType<any>>(
  POSTMainCardAPI,
  {
    isFormDataMethod: false,
  }
);

const getAllMainCardAPI = (queryParams: cardQueryParams = {}): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 1000,
  };

  const queryString = new URLSearchParams(fullParams).toString();
  return `/main-card?${queryString}`;
};

export const useGetMainCard = createGetApiHook<APIResponseType<any>, {}>(getAllMainCardAPI);

const getPublishedCardsAPI = (queryParams: cardQueryParams = {}): string => {
  const { headers: _headers, ...rest } = queryParams;
  const fullParams = {
    ...rest,
    offset: rest.offset ?? 0,
    limit: rest.limit ?? 1000,
  };
  const queryString = new URLSearchParams(fullParams).toString();
  return `/main-card/me?${queryString}`;
};

export const useGetPublishedCards = createGetApiHook<APIResponseType<any>, {}>(
  getPublishedCardsAPI
);

const getMainCardById = ({ id }: { id: number }) => `/main-card/${id}`;

export const useEditMainCard = createPatchApiHook<
  {},
  { id: number; headers?: Record<string, string> },
  APIResponseType<any>
>(getMainCardById, {
  isFormDataMethod: false,
});

export const useDeleteMainCard = createDeleteApiHook<
  {},
  { id: number; headers?: Record<string, string> },
  APIResponseType<any>
>(getMainCardById, {
  isFormDataMethod: false,
});

const getAllTemplateCardsAPI = (queryParams: cardQueryParams = {}): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 1000,
  };

  const queryString = new URLSearchParams(fullParams).toString();
  return `/main-card/templates?${queryString}`;
};

export const useGetAllTemplateCards = createGetApiHook<APIResponseType<any>, {}>(
  getAllTemplateCardsAPI
);

const getTemplatePurchaseStatusAPI = (queryParams: cardQueryParams = {}): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 1000,
  };

  const queryString = new URLSearchParams(fullParams).toString();
  return `/main-card/templates/purchase-status?${queryString}`;
};

export const useGetTemplatePurchaseStatus = createGetApiHook<APIResponseType<any>, {}>(
  getTemplatePurchaseStatusAPI
);

const deletePageAPI = ({ id }: { id: number }) => `/main-card/pages/${id}`;

export const useDeletePage = createDeleteApiHook<{}, { id: number }, APIResponseType<any>>(
  deletePageAPI,
  {
    isFormDataMethod: false,
  }
);

const completeBookingCardAPI = () => `/main-card/booking-action`;

export const useCompleteBookingCard = createPostApiHook<{}, {}, APIResponseType<any>>(
  completeBookingCardAPI,
  {
    isFormDataMethod: false,
  }
);

const getArchivedStaiSessionsAPI = (queryParams: cardQueryParams = {}): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 1000,
  };
  const queryString = new URLSearchParams(fullParams).toString();
  return `/main-card/stai-sessions/archived?${queryString}`;
};

export const useGetArchivedStaiSessions = createGetApiHook<APIResponseType<any>, {}>(
  getArchivedStaiSessionsAPI
);

const getMainCardByIdAPI = ({ card_id, type }: { card_id: number; type: string }) =>
  `/main-card/by-card-id/${card_id}?type=${type}`;

export const useGetMainCardById = createGetApiHook<
  APIResponseType<any>,
  { card_id: number; type: string }
>(getMainCardByIdAPI);

/** Join (purchase) a Fan Club Card. Endpoint: POST /fan-club-cards/:id/join */
const joinFanClubCardAPI = ({ id }: { id: number }) => `/fan-club-cards/${id}/join`;

export const useJoinFanClubCard = createPostApiHook<{}, { id: number }, APIResponseType<any>>(
  joinFanClubCardAPI,
  { isFormDataMethod: false }
);

const getCardByIdAPI = ({
  id,
  extraParams,
}: {
  id: number;
  extraParams?: Record<string, string>;
}) => {
  const path = `/main-card/${id}`;
  if (!extraParams) return path;

  const queryString = new URLSearchParams(extraParams).toString();
  return queryString ? `${path}?${queryString}` : path;
};

const useGetCardByIdBase = createGetApiHook<
  APIResponseType<any>,
  { id: number; headers?: Record<string, string>; extraParams?: Record<string, string> }
>(getCardByIdAPI);

/** Merges `pages` + `page_groups[].blocks` into a flat `pages` array for consumers. */
export const useGetCardById = ((args: Parameters<typeof useGetCardByIdBase>[0]) => {
  const result = useGetCardByIdBase(args);

  const data = useMemo(() => {
    const response = result.data;
    if (!response?.data || typeof response.data !== "object") {
      return response;
    }
    return {
      ...response,
      // data: normalizeMainCardByIdData(response.data as Record<string, unknown>),
      data: response.data as Record<string, unknown>,
    };
  }, [result.data]);

  return { ...result, data } as typeof result;
}) as unknown as typeof useGetCardByIdBase;

const addAdditionalExperienceAPI = () => `/main-card/pages`;

export const useAddAdditionalExperience = createPostApiHook<{}, {}, APIResponseType<any>>(
  addAdditionalExperienceAPI,
  { isFormDataMethod: false }
);

const editPageAPI = ({ id }: { id: number }) => `/main-card/pages/${id}`;

export const useEditPage = createPatchApiHook<{}, { id: number }, APIResponseType<any>>(
  editPageAPI,
  { isFormDataMethod: false }
);

const getCardThumbnailsAPI = (queryParams: cardQueryParams = {}): string => {
  const { headers: _headers, ...rest } = queryParams;
  const fullParams = {
    ...rest,
    offset: rest.offset ?? 0,
    limit: rest.limit ?? 1000,
  };

  const queryString = new URLSearchParams(fullParams).toString();
  return `/main-card/card-thumbnail?${queryString}`;
};

export const useGetCardThumbnails = createGetApiHook<APIResponseType<any>, {}>(
  getCardThumbnailsAPI
);

const getCardThumbnailsPublicAPI = (queryParams: cardQueryParams = {}): string => {
  const { headers: _headers, ...rest } = queryParams;
  const fullParams = {
    ...rest,
    offset: rest.offset ?? 0,
    limit: rest.limit ?? 1000,
  };

  const queryString = new URLSearchParams(fullParams).toString();
  return `/main-card/card-thumbnail-public?${queryString}`;
};

const getCardThumbnailbyCardIdAPI = ({ id }: { id: number }) => `/main-card/card-thumbnail/${id}`;

export const useGetCardThumbnailbyCardId = createGetApiHook<APIResponseType<any>, { id: number }>(
  getCardThumbnailbyCardIdAPI
);

const getCardThumbnailbyCardIdPublicAPI = ({ id }: { id: string }) =>
  `/main-card/card-thumbnail-public/${id}`;

export const useGetCardThumbnailbyCardIdPublic = createGetApiHook<
  APIResponseType<any>,
  { id: string }
>(getCardThumbnailbyCardIdPublicAPI);

export const useGetCardThumbnailsPublic = createGetApiHook<APIResponseType<any>, {}>(
  getCardThumbnailsPublicAPI
);

const getTalentRecentCardsAPI = (queryParams: cardQueryParams = {}): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 1000,
  };
  const queryString = new URLSearchParams(fullParams).toString();
  return `/main-card/recent-by-talent?${queryString}`;
};

export const useGetTalentRecentCards = createGetApiHook<APIResponseType<any>, {}>(
  getTalentRecentCardsAPI
);

const getTalentRecentCardsPublicAPI = (queryParams: cardQueryParams = {}): string => {
  const fullParams = {
    ...queryParams,
    offset: queryParams.offset ?? 0,
    limit: queryParams.limit ?? 1000,
  };
  const queryString = new URLSearchParams(fullParams).toString();
  return `/main-card/recent-by-talent/public?${queryString}`;
};

export const useGetTalentRecentCardsPublic = createGetApiHook<APIResponseType<any>, {}>(
  getTalentRecentCardsPublicAPI
);

const purchaseMainCardAPI = () => `/main-card/purchase`;

export const usePurchaseMainCard = createPostApiHook<
  {},
  {
    main_card_id: number;
  },
  APIResponseType<any>
>(purchaseMainCardAPI, { isFormDataMethod: false });

/** Collect an Impact Card for the signed-in user. */
const ownMainCardAPI = () => `/main-card/own`;

export const useOwnMainCard = createPostApiHook<
  { main_card_id: number },
  {},
  APIResponseType<any>
>(ownMainCardAPI, { isFormDataMethod: false });

const getCardByIdPublicAPI = ({ id }: { id: number }) => `/main-card/public/${id}`;

export const useGetCardByIdPublic = createGetApiHook<APIResponseType<any>, { id: number }>(
  getCardByIdPublicAPI
);
